import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DescriptionIcon from '@mui/icons-material/Description';
import CategoryIcon from '@mui/icons-material/Category';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import { useSelector } from 'react-redux';
import SupportArticleEditor, { createEmptyArticleContent } from './SupportArticleEditor';
import { resolveSupportAssetUrl } from '../utils/supportAssets';

const emptyCategoryForm = {
    id: null,
    name: '',
    description: '',
    order: 0,
    featuredImageUrl: '',
};

const createEmptyArticleForm = () => ({
    id: null,
    title: '',
    summary: '',
    categoryId: '',
    heroImageUrl: '',
    isPublished: true,
    content: createEmptyArticleContent(),
});

const AdminSupportManager = () => {
    const token = useSelector((state) => state.auth.token);

    const [categories, setCategories] = useState([]);
    const [articles, setArticles] = useState([]);

    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [articlesLoading, setArticlesLoading] = useState(true);
    const [selectedArticleId, setSelectedArticleId] = useState(null);

    const [categoryForm, setCategoryForm] = useState(emptyCategoryForm);
    const [articleForm, setArticleForm] = useState(createEmptyArticleForm());
    const [articleContent, setArticleContent] = useState(createEmptyArticleContent());

    const [savingCategory, setSavingCategory] = useState(false);
    const [savingArticle, setSavingArticle] = useState(false);
    const [loadingArticleDetail, setLoadingArticleDetail] = useState(false);
    const [deletingArticle, setDeletingArticle] = useState(false);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [heroUploading, setHeroUploading] = useState(false);
    const [heroUploadError, setHeroUploadError] = useState('');
    const [categoryImageUploading, setCategoryImageUploading] = useState(false);
    const [categoryImageError, setCategoryImageError] = useState('');

    const heroImageInputRef = useRef(null);
    const categoryImageInputRef = useRef(null);

    const authHeaders = useMemo(() => ({
        Authorization: token ? `Bearer ${token}` : '',
    }), [token]);

    const handleSnackbarClose = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const fetchCategories = useCallback(async () => {
        if (!token) return;
        setCategoriesLoading(true);
        try {
            const response = await axios.get('/api/support/admin/categories', {
                headers: authHeaders,
            });
            setCategories(response.data || []);
        } catch (error) {
            setSnackbar({ open: true, severity: 'error', message: 'Failed to load categories.' });
        } finally {
            setCategoriesLoading(false);
        }
    }, [authHeaders, token]);

    const fetchArticles = useCallback(async () => {
        if (!token) return;
        setArticlesLoading(true);
        try {
            const response = await axios.get('/api/support/admin/articles', {
                headers: authHeaders,
            });
            setArticles(response.data || []);
        } catch (error) {
            setSnackbar({ open: true, severity: 'error', message: 'Failed to load articles.' });
        } finally {
            setArticlesLoading(false);
        }
    }, [authHeaders, token]);

    useEffect(() => {
        fetchCategories();
        fetchArticles();
    }, [fetchCategories, fetchArticles]);

    const resetCategoryForm = () => {
        setCategoryForm(emptyCategoryForm);
        setCategoryImageError('');
        if (categoryImageInputRef.current) {
            categoryImageInputRef.current.value = '';
        }
    };

    const resetArticleForm = () => {
        setSelectedArticleId(null);
        setArticleForm(createEmptyArticleForm());
        setArticleContent(createEmptyArticleContent());
    };

    const handleEditCategory = (category) => {
        setCategoryForm({
            id: category.id,
            name: category.name,
            description: category.description || '',
            order: category.order || 0,
            featuredImageUrl: category.featuredImageUrl || '',
        });
        setCategoryImageError('');
        if (categoryImageInputRef.current) {
            categoryImageInputRef.current.value = '';
        }
    };

    const handleDeleteCategory = async (category) => {
        if (!window.confirm(`Delete category "${category.name}"?`)) {
            return;
        }
        try {
            await axios.delete(`/api/support/admin/categories/${category.id}`, {
                headers: authHeaders,
            });
            setCategories((prev) => prev.filter((item) => item.id !== category.id));
            if (categoryForm.id === category.id) {
                resetCategoryForm();
            }
            setSnackbar({ open: true, severity: 'success', message: 'Category deleted.' });
        } catch (error) {
            const message = error.response?.data?.msg || 'Unable to delete this category.';
            setSnackbar({ open: true, severity: 'error', message });
        }
    };

    const handleCategorySubmit = async (event) => {
        event.preventDefault();
        if (!categoryForm.name.trim()) {
            setSnackbar({ open: true, severity: 'error', message: 'Category name is required.' });
            return;
        }
        const payload = {
            name: categoryForm.name.trim(),
            description: (categoryForm.description || '').trim(),
            order: Number(categoryForm.order) || 0,
            featuredImageUrl: (categoryForm.featuredImageUrl || '').trim(),
        };
        setSavingCategory(true);
        try {
            if (categoryForm.id) {
                const response = await axios.put(
                    `/api/support/admin/categories/${categoryForm.id}`,
                    payload,
                    { headers: authHeaders }
                );
                setCategories((prev) => prev.map((item) => (item.id === response.data.id ? response.data : item)));
                setSnackbar({ open: true, severity: 'success', message: 'Category updated.' });
            } else {
                const response = await axios.post(
                    '/api/support/admin/categories',
                    payload,
                    { headers: authHeaders }
                );
                setCategories((prev) => [...prev, response.data]);
                setSnackbar({ open: true, severity: 'success', message: 'Category created.' });
            }
            resetCategoryForm();
        } catch (error) {
            const message = error.response?.data?.msg || 'Failed to save category.';
            setSnackbar({ open: true, severity: 'error', message });
        } finally {
            setSavingCategory(false);
        }
    };

    useEffect(() => {
        if (!selectedArticleId) return;
        const fetchDetail = async () => {
            setLoadingArticleDetail(true);
            try {
                const response = await axios.get(`/api/support/admin/articles/${selectedArticleId}`, {
                    headers: authHeaders,
                });
                const data = response.data;
                setArticleForm({
                    id: data.id,
                    title: data.title,
                    summary: data.summary || '',
                    categoryId: data.categoryId || '',
                    heroImageUrl: data.heroImageUrl || '',
                    isPublished: data.isPublished,
                    content: Array.isArray(data.content) && data.content.length
                        ? data.content
                        : createEmptyArticleContent(),
                });
                setArticleContent(
                    Array.isArray(data.content) && data.content.length
                        ? data.content
                        : createEmptyArticleContent()
                );
            } catch (error) {
                setSnackbar({ open: true, severity: 'error', message: 'Failed to load article details.' });
            } finally {
                setLoadingArticleDetail(false);
            }
        };
        fetchDetail();
    }, [selectedArticleId, authHeaders]);

    const handleSelectArticle = (articleId) => {
        setSelectedArticleId(articleId);
    };

    const handleArticleChange = (field, value) => {
        setArticleForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleArticleSubmit = async (event) => {
        event.preventDefault();
        if (!articleForm.title.trim()) {
            setSnackbar({ open: true, severity: 'error', message: 'Article title is required.' });
            return;
        }
        if (!articleForm.categoryId) {
            setSnackbar({ open: true, severity: 'error', message: 'Please select a category.' });
            return;
        }
        setSavingArticle(true);
        try {
            const payload = {
                title: articleForm.title.trim(),
                summary: articleForm.summary.trim(),
                categoryId: articleForm.categoryId,
                heroImageUrl: articleForm.heroImageUrl.trim(),
                isPublished: !!articleForm.isPublished,
                content: articleContent,
            };
            if (articleForm.id) {
                const response = await axios.put(
                    `/api/support/admin/articles/${articleForm.id}`,
                    payload,
                    { headers: authHeaders }
                );
                setArticles((prev) => prev.map((item) => (item.id === response.data.id ? response.data : item)));
                setSnackbar({ open: true, severity: 'success', message: 'Article updated.' });
            } else {
                const response = await axios.post('/api/support/admin/articles', payload, { headers: authHeaders });
                setArticles((prev) => [response.data, ...prev]);
                setSelectedArticleId(response.data.id);
                setSnackbar({ open: true, severity: 'success', message: 'Article created.' });
            }
        } catch (error) {
            const message = error.response?.data?.msg || 'Failed to save article.';
            setSnackbar({ open: true, severity: 'error', message });
        } finally {
            setSavingArticle(false);
        }
    };

    const handleArticleDelete = async () => {
        if (!articleForm.id) return;
        if (!window.confirm('Delete this article?')) {
            return;
        }
        setDeletingArticle(true);
        try {
            await axios.delete(`/api/support/admin/articles/${articleForm.id}`, { headers: authHeaders });
            setArticles((prev) => prev.filter((item) => item.id !== articleForm.id));
            resetArticleForm();
            setSnackbar({ open: true, severity: 'success', message: 'Article deleted.' });
        } catch (error) {
            const message = error.response?.data?.msg || 'Failed to delete article.';
            setSnackbar({ open: true, severity: 'error', message });
        } finally {
            setDeletingArticle(false);
        }
    };

    const handleUploadImage = useCallback(
        async (file) => {
            try {
                const formData = new FormData();
                formData.append('image', file);
                const response = await axios.post('/api/support/admin/uploads', formData, {
                    headers: {
                        ...authHeaders,
                        'Content-Type': 'multipart/form-data',
                    },
                });
                setSnackbar({ open: true, severity: 'success', message: 'Image uploaded.' });
                return response.data.url;
            } catch (error) {
                const message = error.response?.data?.msg || 'Failed to upload image.';
                setSnackbar({ open: true, severity: 'error', message });
                throw new Error(message);
            }
        },
        [authHeaders]
    );

    const handleHeroImageSelect = async (event) => {
        const file = event.target.files && event.target.files[0];
        if (!file) return;
        setHeroUploadError('');
        setHeroUploading(true);
        try {
            const url = await handleUploadImage(file);
            handleArticleChange('heroImageUrl', url);
        } catch (error) {
            const message = error?.message || 'Failed to upload hero image.';
            setHeroUploadError(message);
        } finally {
            setHeroUploading(false);
            if (heroImageInputRef.current) {
                heroImageInputRef.current.value = '';
            }
        }
    };

    const handleHeroImageRemove = () => {
        handleArticleChange('heroImageUrl', '');
        setHeroUploadError('');
        if (heroImageInputRef.current) {
            heroImageInputRef.current.value = '';
        }
    };

    const handleCategoryImageSelect = async (event) => {
        const file = event.target.files && event.target.files[0];
        if (!file) return;
        setCategoryImageError('');
        setCategoryImageUploading(true);
        try {
            const url = await handleUploadImage(file);
            setCategoryForm((prev) => ({ ...prev, featuredImageUrl: url }));
        } catch (error) {
            const message = error?.message || 'Failed to upload category image.';
            setCategoryImageError(message);
        } finally {
            setCategoryImageUploading(false);
            if (categoryImageInputRef.current) {
                categoryImageInputRef.current.value = '';
            }
        }
    };

    const handleCategoryImageRemove = () => {
        setCategoryForm((prev) => ({ ...prev, featuredImageUrl: '' }));
        setCategoryImageError('');
        if (categoryImageInputRef.current) {
            categoryImageInputRef.current.value = '';
        }
    };

    const hasCategories = categories.length > 0;

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" fontWeight={700} sx={{ mb: 3 }}>
                Support Knowledge Base Manager
            </Typography>

            {!token && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    You must be signed in as an administrator to manage support content.
                </Alert>
            )}

            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #e3f2fd', mb: 3 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <CategoryIcon color="primary" />
                            <Typography variant="h6" fontWeight={600}>
                                Categories
                            </Typography>
                            <Box sx={{ flexGrow: 1 }} />
                            <IconButton onClick={fetchCategories} disabled={categoriesLoading} size="small">
                                {categoriesLoading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
                            </IconButton>
                        </Stack>
                        <List dense>
                            {categories.map((category) => (
                                <ListItem
                                    key={category.id}
                                    secondaryAction={
                                        <Stack direction="row" spacing={1}>
                                            <IconButton size="small" onClick={() => handleEditCategory(category)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                disabled={category.articleCount > 0}
                                                onClick={() => handleDeleteCategory(category)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    }
                                >
                                    <ListItemText
                                        primary={category.name}
                                        secondary={`${category.articleCount} article${category.articleCount === 1 ? '' : 's'}`}
                                    />
                                </ListItem>
                            ))}
                            {!categories.length && !categoriesLoading && (
                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                    No categories yet.
                                </Typography>
                            )}
                        </List>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                            {categoryForm.id ? 'Edit Category' : 'Create Category'}
                        </Typography>
                        <Box component="form" onSubmit={handleCategorySubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <TextField
                                label="Category name"
                                value={categoryForm.name}
                                onChange={(event) => setCategoryForm((prev) => ({ ...prev, name: event.target.value }))}
                                required
                                size="small"
                            />
                            <TextField
                                label="Description"
                                value={categoryForm.description}
                                onChange={(event) => setCategoryForm((prev) => ({ ...prev, description: event.target.value }))}
                                multiline
                                minRows={2}
                                size="small"
                            />
                            <TextField
                                label="Sort order"
                                type="number"
                                value={categoryForm.order}
                                onChange={(event) =>
                                    setCategoryForm((prev) => ({ ...prev, order: Number(event.target.value) || 0 }))
                                }
                                size="small"
                            />
                            <Box>
                                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                                    Category image
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    Optional image shown on the support page.
                                </Typography>
                                <input
                                    type="file"
                                    accept="image/*"
                                    ref={categoryImageInputRef}
                                    style={{ display: 'none' }}
                                    onChange={handleCategoryImageSelect}
                                />
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() =>
                                            categoryImageInputRef.current && categoryImageInputRef.current.click()
                                        }
                                        disabled={categoryImageUploading}
                                    >
                                        {categoryImageUploading ? 'Uploading…' : 'Upload image'}
                                    </Button>
                                    {categoryForm.featuredImageUrl && (
                                        <Button
                                            type="button"
                                            variant="text"
                                            color="error"
                                            size="small"
                                            onClick={handleCategoryImageRemove}
                                        >
                                            Remove
                                        </Button>
                                    )}
                                </Stack>
                                {categoryImageError && (
                                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                                        {categoryImageError}
                                    </Typography>
                                )}
                                {categoryForm.featuredImageUrl && (
                                    <Box sx={{ mt: 1.5 }}>
                                        <img
                                            src={resolveSupportAssetUrl(categoryForm.featuredImageUrl)}
                                            alt="Category preview"
                                            style={{
                                                width: '100%',
                                                maxHeight: 160,
                                                objectFit: 'cover',
                                                borderRadius: 12,
                                                border: '1px solid rgba(0,0,0,0.08)',
                                            }}
                                        />
                                    </Box>
                                )}
                            </Box>
                            <Stack direction="row" spacing={1}>
                                <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={savingCategory}>
                                    {categoryForm.id ? 'Update' : 'Create'}
                                </Button>
                                <Button type="button" variant="text" onClick={resetCategoryForm}>
                                    Cancel
                                </Button>
                            </Stack>
                        </Box>
                    </Paper>

                    <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #e0f2f1' }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <DescriptionIcon color="primary" />
                            <Typography variant="h6" fontWeight={600}>
                                Articles
                            </Typography>
                            <Box sx={{ flexGrow: 1 }} />
                            <IconButton onClick={fetchArticles} disabled={articlesLoading} size="small">
                                {articlesLoading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
                            </IconButton>
                            <Button variant="outlined" startIcon={<AddIcon />} onClick={resetArticleForm}>
                                New
                            </Button>
                        </Stack>
                        <List dense sx={{ maxHeight: 360, overflowY: 'auto' }}>
                            {articles.map((articleItem) => (
                                <ListItemButton
                                    key={articleItem.id}
                                    onClick={() => handleSelectArticle(articleItem.id)}
                                    selected={articleItem.id === selectedArticleId}
                                    sx={{ borderRadius: 2 }}
                                >
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                        <Chip
                                            size="small"
                                            label={articleItem.isPublished ? 'Published' : 'Draft'}
                                            color={articleItem.isPublished ? 'success' : 'default'}
                                        />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={articleItem.title}
                                        secondary={
                                            articleItem.category
                                                ? `${articleItem.category.name} • ${new Intl.DateTimeFormat(undefined, {
                                                      month: 'short',
                                                      day: 'numeric',
                                                  }).format(new Date(articleItem.updatedAt))}`
                                                : 'Uncategorized'
                                        }
                                    />
                                </ListItemButton>
                            ))}
                            {!articles.length && !articlesLoading && (
                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                    No articles yet.
                                </Typography>
                            )}
                        </List>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e8eaf6', minHeight: 560 }}>
                        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                            {articleForm.id ? 'Edit Article' : 'Create Article'}
                        </Typography>

                        {loadingArticleDetail ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Box component="form" onSubmit={handleArticleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    label="Title"
                                    value={articleForm.title}
                                    onChange={(event) => handleArticleChange('title', event.target.value)}
                                    required
                                />
                                <TextField
                                    label="Summary"
                                    value={articleForm.summary}
                                    onChange={(event) => handleArticleChange('summary', event.target.value)}
                                    multiline
                                    minRows={2}
                                />
                                <FormControl fullWidth>
                                    <InputLabel id="support-category-label">Category</InputLabel>
                                    <Select
                                        labelId="support-category-label"
                                        label="Category"
                                        value={articleForm.categoryId}
                                        onChange={(event) => handleArticleChange('categoryId', event.target.value)}
                                        required
                                    >
                                        {categories.map((category) => (
                                            <MenuItem key={category.id} value={category.id}>
                                                {category.name}
                                            </MenuItem>
                                        ))}
                                        {!hasCategories && <MenuItem disabled>No categories available</MenuItem>}
                                    </Select>
                                </FormControl>
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                                        Hero image
                                    </Typography>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        ref={heroImageInputRef}
                                        style={{ display: 'none' }}
                                        onChange={handleHeroImageSelect}
                                    />
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                        <Button
                                            variant="outlined"
                                            onClick={() => heroImageInputRef.current && heroImageInputRef.current.click()}
                                            disabled={heroUploading}
                                        >
                                            {heroUploading ? 'Uploading…' : 'Upload image'}
                                        </Button>
                                        {articleForm.heroImageUrl && (
                                            <Button variant="text" color="error" onClick={handleHeroImageRemove}>
                                                Remove
                                            </Button>
                                        )}
                                    </Stack>
                                    {heroUploadError && (
                                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                                            {heroUploadError}
                                        </Typography>
                                    )}
                                    {articleForm.heroImageUrl && (
                                        <Box sx={{ mt: 2 }}>
                                            <img
                                                src={resolveSupportAssetUrl(articleForm.heroImageUrl)}
                                                alt="Hero preview"
                                                style={{
                                                    width: '100%',
                                                    maxHeight: 220,
                                                    objectFit: 'cover',
                                                    borderRadius: 12,
                                                    border: '1px solid rgba(0,0,0,0.08)',
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={articleForm.isPublished}
                                            onChange={(event) => handleArticleChange('isPublished', event.target.checked)}
                                        />
                                    }
                                    label={articleForm.isPublished ? 'Published' : 'Draft'}
                                />

                                <SupportArticleEditor value={articleContent} onChange={setArticleContent} onUploadImage={handleUploadImage} />

                                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        startIcon={<SaveIcon />}
                                        disabled={savingArticle || !hasCategories}
                                    >
                                        {articleForm.id ? 'Update Article' : 'Create Article'}
                                    </Button>
                                    {articleForm.id && (
                                        <Button
                                            type="button"
                                            variant="outlined"
                                            color="error"
                                            startIcon={<DeleteIcon />}
                                            onClick={handleArticleDelete}
                                            disabled={deletingArticle}
                                        >
                                            Delete
                                        </Button>
                                    )}
                                    <Button type="button" variant="text" onClick={resetArticleForm}>
                                        Reset
                                    </Button>
                                </Stack>
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleSnackbarClose}>
                <Alert severity={snackbar.severity} onClose={handleSnackbarClose} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AdminSupportManager;
